import { Check, Copy, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { renderNdPrompt, useNdPrompt } from "@/hooks/use-nd-prompt";

// navigator.clipboard fails in cross-origin iframes (NymblStudio) and on insecure
// origins. Fall back to a textarea + execCommand("copy") which works in those cases
// as long as we're inside a user-gesture handler.
async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy path
    }
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

interface CopyPromptButtonProps {
  solutionName?: string;
  variant?: React.ComponentProps<typeof Button>["variant"];
  size?: React.ComponentProps<typeof Button>["size"];
  label?: string;
  className?: string;
}

export function CopyPromptButton({
  solutionName,
  variant = "outline",
  size = "default",
  label = "Copy prompt",
  className,
}: CopyPromptButtonProps) {
  const { data, isLoading, isError } = useNdPrompt();
  const [copied, setCopied] = useState(false);

  const onClick = async () => {
    if (!data) return;
    const rendered = renderNdPrompt(data.value, { solutionName });
    if (await copyToClipboard(rendered)) {
      setCopied(true);
      toast.success("ND prompt copied — paste it into Claude or Cursor");
      window.setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Could not access clipboard");
    }
  };

  const disabled = isLoading || isError || !data;

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={onClick}
      disabled={disabled}
      className={className}
      title="Copy a prompt you can paste into Claude/Cursor to generate the architecture file"
    >
      {copied ? <Check className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
      {copied ? "Copied" : label}
      {!copied && <Copy className="h-3.5 w-3.5 opacity-60" />}
    </Button>
  );
}
